// src/types/dnl.ts
/**
 * Double Number Line (DNL) Enhancement Types
 * Phase 1: Custom/Sparse Points System
 * 
 * Based on DNL Enhancement Specification v1.0
 */

import type { Fraction } from './redux';

// =============================================================================
// CUSTOM TICK ENHANCEMENTS FOR DNL
// =============================================================================

/**
 * Enhanced CustomTick for DNL functionality
 * Extends the basic CustomTick with line assignment and styling
 */
export interface DNLCustomTick {
  id: string;
  /** Numeric value on the number line */
  value: number;
  /** Optional custom label text (e.g., "100g", "?") */
  label?: string;
  /** Which line this tick belongs to */
  line: 'primary' | 'secondary' | 'both';
  /** Whether to show the tick mark */
  showTick: boolean;
  /** Whether to show the label */
  showLabel: boolean;
  /** Visual styling */
  style: 'normal' | 'highlighted' | 'question';
  /** Optional custom colour */
  colour?: string;
  /** Timestamp for ordering */
  createdAt: number;
  /** Optional link to corresponding tick on other line */
  linkedTickId?: string;
}

// =============================================================================
// SPARSE TICK MODE
// =============================================================================

/**
 * Configuration for sparse tick mode
 * When enabled, hides regular auto-generated ticks and shows only custom points
 */
export interface SparseTickMode {
  /** Whether sparse mode is enabled */
  enabled: boolean;
  /** Hide auto-generated ticks when enabled */
  hideRegularTicks: boolean;
  /** Custom ticks to display */
  customTicks: DNLCustomTick[];
  /** Always show zero (enforced for DNL) */
  showZero: boolean;
  /** Next ID for new custom ticks */
  nextId: number;
}

// =============================================================================
// LINE LABELS
// =============================================================================

/**
 * Configuration for contextual line labels
 * e.g., "People", "ml sauce"
 */
export interface LineLabel {
  /** Main label text */
  text: string;
  /** Optional subtext (e.g., unit) */
  subtext?: string;
  /** Position relative to line */
  position: 'left' | 'right';
  /** Whether the label is visible */
  visible: boolean;
}

// =============================================================================
// UNIT SUFFIX
// =============================================================================

/**
 * Configuration for unit suffixes on labels
 * Auto-appended to all labels on a line (e.g., "g", "ml")
 */
export interface UnitSuffix {
  /** The unit text */
  text: string;
  /** Position relative to the number */
  position: 'before' | 'after';
  /** Whether to show on labels */
  enabled: boolean;
}

// =============================================================================
// DNL TOOLS STATE
// =============================================================================

/**
 * Complete DNL Tools state
 * Nested within SecondaryLineState
 */
export interface DNLToolsState {
  /** Sparse tick mode configuration */
  sparseMode: SparseTickMode;
  /** Primary line label */
  primaryLabel: LineLabel;
  /** Secondary line label */
  secondaryLabel: LineLabel;
  /** Unit suffix for primary line */
  primaryUnit: UnitSuffix;
  /** Unit suffix for secondary line */
  secondaryUnit: UnitSuffix;
}

// =============================================================================
// ADD POINT DIALOG STATE
// =============================================================================

/**
 * State for the Add Point dialog
 */
export interface AddPointDialogState {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Which line to add the point to */
  targetLine: 'primary' | 'secondary';
  /** Pre-filled value (e.g., from click position) */
  initialValue?: number;
}

// =============================================================================
// DEFAULT VALUES
// =============================================================================

/**
 * Default sparse tick mode state
 */
export const defaultSparseTickMode: SparseTickMode = {
  enabled: false,
  hideRegularTicks: true,
  customTicks: [],
  showZero: true,
  nextId: 1,
};

/**
 * Default line label state
 */
export const defaultLineLabel: LineLabel = {
  text: '',
  subtext: undefined,
  position: 'left',
  visible: false,
};

/**
 * Default unit suffix state
 */
export const defaultUnitSuffix: UnitSuffix = {
  text: '',
  position: 'after',
  enabled: false,
};

/**
 * Default DNL tools state
 */
export const defaultDNLToolsState: DNLToolsState = {
  sparseMode: defaultSparseTickMode,
  primaryLabel: { ...defaultLineLabel },
  secondaryLabel: { ...defaultLineLabel },
  primaryUnit: { ...defaultUnitSuffix },
  secondaryUnit: { ...defaultUnitSuffix },
};

// =============================================================================
// TYPE GUARDS
// =============================================================================

/**
 * Type guard to check if a tick is a DNL custom tick
 */
export function isDNLCustomTick(tick: unknown): tick is DNLCustomTick {
  return (
    typeof tick === 'object' &&
    tick !== null &&
    'id' in tick &&
    'value' in tick &&
    'line' in tick &&
    'showTick' in tick &&
    'showLabel' in tick &&
    'style' in tick
  );
}

/**
 * Check if sparse mode should render a value
 */
export function shouldRenderInSparseMode(
  value: number,
  sparseMode: SparseTickMode
): boolean {
  if (!sparseMode.enabled) return true;
  
  // Always show zero if configured
  if (sparseMode.showZero && Math.abs(value) < 1e-10) return true;
  
  // Check if value matches any custom tick
  return sparseMode.customTicks.some(
    tick => Math.abs(tick.value - value) < 1e-10 && tick.showTick
  );
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Format a value with unit suffix
 */
export function formatValueWithUnit(
  value: string | number,
  unit: UnitSuffix
): string {
  if (!unit.enabled || !unit.text) {
    return String(value);
  }
  
  return unit.position === 'before'
    ? `${unit.text}${value}`
    : `${value}${unit.text}`;
}

/**
 * Get the label for a custom tick
 */
export function getCustomTickLabel(
  tick: DNLCustomTick,
  unit: UnitSuffix
): string {
  // If tick has a custom label, use it
  if (tick.label) {
    return tick.label;
  }
  
  // Otherwise, format the value with unit
  return formatValueWithUnit(tick.value, unit);
}

/**
 * Calculate corresponding value on the other line using scale factor
 */
export function calculateCorrespondingValue(
  value: number,
  scaleFactor: Fraction,
  fromLine: 'primary' | 'secondary'
): number {
  const factor = scaleFactor.n / scaleFactor.d;
  
  return fromLine === 'primary'
    ? value * factor  // primary to secondary
    : value / factor; // secondary to primary
}
